import { useState } from 'react';
import { useEmbalagens, useCriarEmbalagem, useAtualizarEmbalagem, useDeletarEmbalagem, useBulkDeletarEmbalagens } from '@/hooks/useInsumos';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/Dialog';
import { Plus, Pencil, Trash2, CheckSquare, Square, Trash } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import type { Embalagem } from '@/types';

export function Embalagens() {
  const { data: embalagens, isLoading } = useEmbalagens();
  const criarEmbalagem = useCriarEmbalagem();
  const atualizarEmbalagem = useAtualizarEmbalagem();
  const deletarEmbalagem = useDeletarEmbalagem();
  const bulkDeletarEmbalagem = useBulkDeletarEmbalagens();
  
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedInsumo, setSelectedInsumo] = useState<Embalagem | null>(null);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'OUTRO',
    dimensoes: '',
    custoUnitario: '',
    fornecedor: '',
    quantidadeEstoque: '0',
  });

  const handleEdit = (insumo: Embalagem) => {
    setSelectedInsumo(insumo);
    setFormData({
      nome: insumo.nome,
      tipo: insumo.tipo,
      dimensoes: insumo.dimensoes || '',
      custoUnitario: String(insumo.custoUnitario),
      fornecedor: insumo.fornecedor || '',
      quantidadeEstoque: String(insumo.quantidadeEstoque),
    });
    setOpen(true);
  };

  const cleanNumber = (val: string | number) => {
    if (typeof val === 'string') {
      const cleaned = val.replace('.', '').replace(',', '.');
      return parseFloat(cleaned) || 0;
    }
    return val;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...formData,
      custoUnitario: cleanNumber(formData.custoUnitario),
      quantidadeEstoque: cleanNumber(formData.quantidadeEstoque),
    };

    if (selectedInsumo) {
      await atualizarEmbalagem.mutateAsync({ id: selectedInsumo.id, data: payload });
    } else {
      await criarEmbalagem.mutateAsync(payload);
    }
    
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setSelectedInsumo(null);
    setFormData({ nome: '', tipo: 'OUTRO', dimensoes: '', custoUnitario: '', fornecedor: '', quantidadeEstoque: '0' });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este insumo?')) {
      await deletarEmbalagem.mutateAsync(id);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === (embalagens?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(embalagens?.map((p: any) => p.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Deseja excluir os ${selectedIds.length} insumos selecionados?`)) {
      bulkDeletarEmbalagem.mutate(selectedIds, {
        onSuccess: (response: any) => {
          setSelectedIds([]);
          alert(response?.data?.message || 'Exclusão em massa concluída!');
        },
        onError: () => {
          alert('Erro ao realizar exclusão em massa');
        }
      });
    }
  };

  const tiposInsumo = [
    { value: 'CAIXA_PAPELAO', label: '📦 Caixa de Papelão' },
    { value: 'SACO_BOLHA', label: '🫧 Saco de Bolha' },
    { value: 'FITA_ADESIVA', label: '🩹 Fita Adesiva' },
    { value: 'ENCHIMENTO', label: '☁️ Enchimento' },
    { value: 'FERRAGEM', label: '⛓️ Ferragem (Argolas, etc.)' },
    { value: 'ACESSORIO', label: '✨ Acessório' },
    { value: 'CONSUMIVEL', label: '🔄 Consumível' },
    { value: 'OUTRO', label: '➕ Outro' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Insumos Gerais</h1>
          <p className="text-gray-500">Gerencie caixas, fitas, argolas e outros componentes</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50 transition-colors"
              onClick={handleBulkDelete}
              disabled={bulkDeletarEmbalagem.isPending}
            >
              <Trash className="h-4 w-4 mr-2" />
              Excluir ({selectedIds.length})
            </Button>
          )}
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <DialogTrigger asChild>
              <Button variant="nuvem" leftIcon={<Plus className="h-4 w-4" />}>
                Novo Insumo
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>{selectedInsumo ? 'Editar Insumo Geral' : 'Cadastrar Insumo Geral'}</DialogTitle>
              <DialogDescription>
                Preencha os dados técnicos do componente abaixo.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-5 py-4">
              <Input
                label="Nome do Insumo (ex: Argolas de Chaveiro - 50 un)"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">Tipo de Insumo</label>
                  <select
                    className="w-full flex h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-sans"
                    value={formData.tipo}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                    required
                  >
                    {tiposInsumo.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Dimensões (opcional)"
                  value={formData.dimensoes}
                  onChange={(e) => setFormData({ ...formData, dimensoes: e.target.value })}
                  placeholder="ex: 20mm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Custo Unitário (R$)"
                  type="number"
                  step="0.0001"
                  value={formData.custoUnitario}
                  onChange={(e) => setFormData({ ...formData, custoUnitario: e.target.value })}
                  required
                />
                <Input
                  label="Estoque Inicial"
                  type="number"
                  value={formData.quantidadeEstoque}
                  onChange={(e) => setFormData({ ...formData, quantidadeEstoque: e.target.value })}
                />
              </div>
              <Input
                label="Fornecedor"
                value={formData.fornecedor}
                onChange={(e) => setFormData({ ...formData, fornecedor: e.target.value })}
              />
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" variant="nuvem" isLoading={criarEmbalagem.isPending || atualizarEmbalagem.isPending}>
                  {selectedInsumo ? 'Atualizar Insumo' : 'Salvar Insumo'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-4 px-4 w-10">
                      <button onClick={toggleSelectAll} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                        {selectedIds.length === (embalagens?.length || 0) && selectedIds.length > 0 ? (
                          <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Nome</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Tipo</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Dimensões</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Fornecedor</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Custo</th>
                    <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Estoque</th>
                    <th className="py-4 px-6 text-right text-sm font-medium text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {embalagens?.map((embalagem: Embalagem) => (
                    <tr key={embalagem.id} className={`border-b border-gray-200/50 hover:bg-gray-50 transition-colors ${selectedIds.includes(embalagem.id) ? 'bg-orange-50/30' : ''}`}>
                      <td className="py-4 px-4">
                        <button onClick={() => toggleSelect(embalagem.id)} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                          {selectedIds.includes(embalagem.id) ? (
                            <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-6 font-medium text-gray-900">{embalagem.nome}</td>
                      <td className="py-4 px-6 text-gray-500 text-xs font-mono">{embalagem.tipo}</td>
                      <td className="py-4 px-6 text-gray-500">{embalagem.dimensoes}</td>
                      <td className="py-4 px-6 text-gray-500">{embalagem.fornecedor}</td>
                      <td className="py-4 px-6 text-gray-900">{formatCurrency(embalagem.custoUnitario)}</td>
                      <td className="py-4 px-6">
                        <span className={embalagem.quantidadeEstoque < 50 ? 'text-red-600 font-bold' : 'text-gray-900'}>
                          {embalagem.quantidadeEstoque}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(embalagem)}>
                            <Pencil className="h-4 w-4 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(embalagem.id)}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
